import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { ChangeFullnameFormProps } from './types';
import { useDisclosure } from '@src/core/hooks';
import { useUpdateProfileMutation } from '@src/features/profile/redux';
import { IUserChangeFullnameForm } from '@src/features/profile/entites';
import { Controller, useForm } from 'react-hook-form';
import { EMPTY_STRING, EMPTY_VALUE } from '@src/core/config';
import { ArcButton, ArcDialog, ArcIconButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { toast } from 'react-toastify';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  PROFILE_NAME_MAX_LENGTH,
  profileNameInputKeyDown,
  sanitizeProfileNameInput,
  validateProfileNameField,
} from '@src/features/profile/utils/profile-name-rules';

const ChangeFullnameForm: React.FC<ChangeFullnameFormProps> = (props) => {
  const { profile } = props;
  const { open, show, hide } = useDisclosure();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const { control, formState: { errors }, handleSubmit, reset } = useForm<IUserChangeFullnameForm>({
    defaultValues: {
      lastname: profile?.lastname ?? EMPTY_VALUE,
      firstname: profile?.firstname ?? EMPTY_VALUE,
    },
  });

  React.useEffect(() => {
    if (!open || !profile) return;
    reset({
      firstname: profile.firstname ?? EMPTY_VALUE,
      lastname: profile.lastname ?? EMPTY_VALUE,
    });
  }, [open, profile, reset]);

  const onUpdateProfile = (form: IUserChangeFullnameForm) => {
    const firstname = form.firstname.trim();
    const lastname = form.lastname.trim();
    updateProfile({
      lastname,
      firstname,
    }).unwrap().then(() => {
      toast.success('UPDATE PROFILE SUCCESS TEXT');
      hide();
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0019', extra: { action: 'updateFullName' } });
      toast.error('Failed to update name');
    });
  };


  return (
    <React.Fragment>
      <ArcStack 
        spacing={1}
        direction='row' 
        alignItems='center'>
        <ArcText.h2> 
          {profile?.firstname || EMPTY_STRING} {profile?.lastname || EMPTY_STRING} 
        </ArcText.h2>
        <ArcIconButton size='small' onClick={show}>
          <EditIcon sx={{ fontSize: 16 }} />
        </ArcIconButton>
      </ArcStack>
      <ArcDialog open={open} onClose={hide} title='Change Name'>
        <ArcStack 
          p={2} 
          spacing={2} 
          component='form' 
          onSubmit={handleSubmit(onUpdateProfile)}>

          <Controller
            name='firstname'
            control={control}
            rules={{ validate: validateProfileNameField }}
            render={({ field }) => (
              <ArcOutlinedInput
                {...field}
                label='First Name'
                autoComplete='off'
                maxLength={PROFILE_NAME_MAX_LENGTH}
                error={Boolean(errors.firstname)}
                helperText={errors.firstname?.message}
                onChange={(e) => field.onChange(sanitizeProfileNameInput(e.target.value))}
                onKeyDown={profileNameInputKeyDown}
              />
            )}
          />

          <Controller
            name='lastname'
            control={control}
            rules={{ validate: validateProfileNameField }}
            render={({ field }) => (
              <ArcOutlinedInput
                {...field}
                label='Last Name'
                autoComplete='off'
                maxLength={PROFILE_NAME_MAX_LENGTH}
                error={Boolean(errors.lastname)}
                helperText={errors.lastname?.message}
                onChange={(e) => field.onChange(sanitizeProfileNameInput(e.target.value))}
                onKeyDown={profileNameInputKeyDown}
              />
            )}
          />

          <ArcButton.brand 
            type='submit' 
            isLoading={isLoading}>
            Save
          </ArcButton.brand>

        </ArcStack>
      </ArcDialog>
    </React.Fragment>
  );
};

export default ChangeFullnameForm;